import React from 'react';

import {
  Avatar,
  Card,
  Flexbox,
  FontAwesomeIcon,
  StatusLozenge,
  Text,
  Tooltip,
} from '@schoolinks/ui-components';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import rehypeRaw from 'rehype-raw';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';

import LazyLoadImage from 'components/Common/LazyLoadImage';
import RenderMarkdown from 'components/Common/RenderMarkdown';
import { ShortMonthDayYearComma } from 'constants/dateTimeFormats';
import Api from 'core/api';
import useFetch from 'hooks/useFetch';
import useUserRedux from 'hooks/useUserRedux';
import dayjs from 'services/dayjs';
import { ensureObject } from 'utils/objects';

import Placeholder from 'modules/CredentialWallet/components/Placeholder';
import ShowPageHeader from 'modules/CredentialWallet/components/ShowPageHeader';

import s from './CredentialShowPage.css';

const CredentialShowPage = () => {
  const { t } = useTranslation();
  const { type, credentialId, studentId } = useParams();

  const { data: user } = useUserRedux();

  const { data: credential, isLoading: isLoadingCredential } = useFetch({
    apiFunc: Api.clr.getCredentialDetails,
    apiFuncName: 'clr.getCredentialDetails',
    apiFuncParams: {
      type,
      credentialId,
    },
    filters: { student: studentId },
    options: { enabled: Boolean(user && type && credentialId) },
  });

  const {
    credentialName,
    credentialImage,
    credentialDescription,
    credentialNarrative,
    credentialAwarded,
    credentialIssuerName,
    credentialIssuerWebsite,
    credentialIssuerLogo,
    credentialIsVerified,
    credentialValidUntil,
    credentialCredentialType,
  } = ensureObject(credential);

  return (
    <>
      <ShowPageHeader
        credentialName={credentialName}
        isPublic={false}
        setIsPublic={noop}
        setCredentialVisibility={noop}
        type={type}
        credentialId={credentialId}
        copyToClipboard={noop}
        exportCredential={noop}
        shareCredential={noop}
        credentialCredentialType={credentialCredentialType}
        hideActions
      />
      <Flexbox className={s.content} justifyContent="center" p={3} width="100%">
        <Flexbox className={s.cardContainer}>
          <Card>
            <Flexbox className={s.cardContents} columnGap={4} p={4} rowGap={4}>
              <Flexbox>
                {credentialImage ? (
                  <LazyLoadImage
                    className={s.logo}
                    src={credentialImage}
                    alt={credentialName}
                  />
                ) : (
                  <Placeholder
                    width={200}
                    height={200}
                    isLoadingCredential={isLoadingCredential}
                  />
                )}
              </Flexbox>
              <Flexbox flexDirection="column" width="100%">
                <Flexbox mb={3} width="100%">
                  <Flexbox className={s.third} flexDirection="column">
                    <Text
                      className={s.sectionHeading}
                      color="grayDark"
                      variant="bodySm400"
                    >
                      {t('CredentialWallet.CredentialShowPage.IssuedDate')}
                    </Text>
                    <Text variant="bodyReg400">
                      {credentialAwarded
                        ? dayjs(credentialAwarded).format(
                            ShortMonthDayYearComma,
                          )
                        : '-'}
                    </Text>
                  </Flexbox>
                  <Flexbox className={s.third} flexDirection="column">
                    <Text
                      className={s.sectionHeading}
                      color="grayDark"
                      variant="bodySm400"
                    >
                      {t('CredentialWallet.CredentialShowPage.ExpirationDate')}
                    </Text>
                    <Text variant="bodyReg400">
                      {credentialValidUntil
                        ? dayjs(credentialValidUntil).format(
                            ShortMonthDayYearComma,
                          )
                        : t('CredentialWallet.Constants.DoesNotExpireText')}
                    </Text>
                  </Flexbox>
                  <Flexbox className={s.third} flexDirection="column">
                    <Text
                      className={s.sectionHeading}
                      color="grayDark"
                      variant="bodySm400"
                    >
                      {t('CredentialWallet.CredentialShowPage.Verification')}
                    </Text>
                    <Flexbox alignItems="center" columnGap={0.5}>
                      <StatusLozenge
                        color={credentialIsVerified ? 'green' : 'orange'}
                        label={
                          credentialIsVerified
                            ? t('CredentialWallet.CredentialShowPage.Verified')
                            : t(
                                'CredentialWallet.CredentialShowPage.Unverified',
                              )
                        }
                      />

                      <Tooltip
                        placement="bottom"
                        title={
                          credentialIsVerified
                            ? t('CredentialWallet.Constants.VerifiedTooltip')
                            : t('CredentialWallet.Constants.UnverifiedTooltip')
                        }
                      >
                        <div>
                          <FontAwesomeIcon
                            color="grayDark"
                            icon={faCircleInfo}
                            size="lg"
                          />
                        </div>
                      </Tooltip>
                    </Flexbox>
                  </Flexbox>
                </Flexbox>
                <Text
                  className={s.sectionHeading}
                  color="grayDark"
                  variant="bodySm400"
                >
                  {t('CredentialWallet.CredentialShowPage.Issuer')}
                </Text>
                <Flexbox alignItems="center" columnGap={1} mb={3}>
                  <Avatar size="md" src={credentialIssuerLogo} />
                  <Flexbox flexDirection="column">
                    <Text variant="bodyReg400">{credentialIssuerName}</Text>
                    {credentialIssuerWebsite && (
                      <Text variant="captionReg700">
                        {credentialIssuerWebsite}
                      </Text>
                    )}
                  </Flexbox>
                </Flexbox>
                <Text
                  className={s.sectionHeading}
                  color="grayDark"
                  variant="bodySm400"
                >
                  {t('CredentialWallet.CredentialShowPage.Description')}
                </Text>
                <Text mb={3} variant="bodyReg400">
                  {credentialDescription ? (
                    <RenderMarkdown rehypePlugins={[rehypeRaw]}>
                      {credentialDescription}
                    </RenderMarkdown>
                  ) : (
                    '-'
                  )}
                </Text>
                <Text
                  className={s.sectionHeading}
                  color="grayDark"
                  variant="bodySm400"
                >
                  {t('CredentialWallet.CredentialShowPage.Narrative')}
                </Text>
                <Text variant="bodyReg400">
                  {credentialNarrative ? (
                    <RenderMarkdown rehypePlugins={[rehypeRaw]}>
                      {credentialNarrative}
                    </RenderMarkdown>
                  ) : (
                    '-'
                  )}
                </Text>
              </Flexbox>
            </Flexbox>
          </Card>
        </Flexbox>
      </Flexbox>
    </>
  );
};

export default CredentialShowPage;
